import Material from '../materials/Material.js';
import MaterialFactory from '../materials/MaterialFactory.js';
import Impostor from '../rendering/Impostor.js';
import TextureGenerator from '../utils/TextureGenerator.js';
import Cuboid from '../rendering/Cuboid.js';
import GUIBuilder from './widgets/GUIBuilder.js';
import ColorPropertyPanel from './panels/ColorPropertyPanel.js';
import TexturePropertyPanel from './panels/TexturePropertyPanel.js';
import GradientPropertyPanel from './panels/GradientPropertyPanel.js';
import PatternPropertyPanel from './panels/PatternPropertyPanel.js';
import VideoPropertyPanel from './panels/VideoPropertyPanel.js';
import TextPropertyPanel from './panels/TextPropertyPanel.js';
import EffectsPropertyPanel from './panels/EffectsPropertyPanel.js';
import WebpagePropertyPanel from './panels/WebpagePropertyPanel.js';
import Debug from '../utils/Debug.js';

/**
 * MaterialEditor - Modular lil-gui style material editor
 * Refactored to use GUIBuilder components and PropertyPanels
 */
export default class MaterialEditor {
  /**
   * Create a new MaterialEditor
   * @param {World} world - The world instance
   * @param {HTMLElement} container - Container element to render into (from SidebarManager)
   */
  constructor(world, container) {
    this.world = world;
    this.container = container;
    this.materialFactory = new MaterialFactory();
    this.textureGenerator = new TextureGenerator();
    this.selectedCuboid = null;
    this.selectedFace = 'top';
    this.propertyPanels = {};

    // Preview system state
    // Stores original materials before preview: Map<"cuboidKey|face", materialCopy>
    this.originalMaterials = new Map();
    // Track if we have uncommitted preview changes
    this.hasUnappliedChanges = false;

    this.initializeUI();
  }

  /**
   * Initialize the UI using GUIBuilder components
   */
  initializeUI() {
    // Create content wrapper
    this.content = document.createElement('div');
    this.content.className = 'gui-content material-editor-content';

    // Create sections
    this.createSelectionSection();
    this.createGroupSection();
    this.createMaterialSection();
    this.createBorderSection();

    // Apply button at bottom - add specific class for CSS targeting
    const applyBtn = GUIBuilder.createButton('Apply Material', 'primary', true);
    applyBtn.classList.add('material-apply-btn');
    applyBtn.addEventListener('click', () => this.applyMaterial());

    this.container.appendChild(this.content);
    this.container.appendChild(applyBtn);

    // Initialize data
    this.updateCuboidList();
  }

  /**
   * Create selection section (cuboid + face selectors)
   */
  createSelectionSection() {
    const section = GUIBuilder.createColorCodedSection('Selection', 'info', false);

    // Cuboid selector
    this.cuboidSelector = GUIBuilder.createSelect([], '');
    this.cuboidSelector.addEventListener('change', () => this.onCuboidSelected());
    section.body.appendChild(GUIBuilder.createRow('Cuboid', this.cuboidSelector));

    // Face selector
    this.faceSelector = GUIBuilder.createSelect([
      { value: 'top', label: 'Top' },
      { value: 'bottom', label: 'Bottom' },
      { value: 'left', label: 'Left' },
      { value: 'right', label: 'Right' },
      { value: 'front', label: 'Front' },
      { value: 'back', label: 'Back' }
    ], 'top');
    this.faceSelector.addEventListener('change', () => {
      this.updateFaceSelection();
      this.updateMaterialTypeSelector();
    });
    section.body.appendChild(GUIBuilder.createRow('Face', this.faceSelector));

    // Delete cuboid button
    const deleteBtn = GUIBuilder.createButton('Delete Cuboid', 'danger', true);
    deleteBtn.style.marginTop = '8px';
    deleteBtn.addEventListener('click', () => this.deleteSelectedCuboid());
    section.body.appendChild(deleteBtn);

    // Delete vegetation on cuboid button (hidden when no vegetation)
    this._deleteVegetationBtn = GUIBuilder.createButton('Delete Vegetation On Cuboid', 'warning', true);
    this._deleteVegetationBtn.style.marginTop = '4px';
    this._deleteVegetationBtn.style.display = 'none';
    this._deleteVegetationBtn.addEventListener('click', () => this.deleteVegetationOnCuboid());
    section.body.appendChild(this._deleteVegetationBtn);

    this.content.appendChild(section.section);
  }

  /**
   * Delete the currently selected cuboid
   */
  deleteSelectedCuboid() {
    if (!this.selectedCuboid) {
      alert('Please select a cuboid first');
      return;
    }

    if (!confirm('Delete this cuboid?')) {
      return;
    }

    const cuboid = this.selectedCuboid;
    const key = cuboid.getKey();
    const ux = cuboid.x, uy = cuboid.y, uz = cuboid.z;
    const uw = cuboid.width, uh = cuboid.height, ud = cuboid.depth;
    const texOrigin = cuboid.textureOrigin ? { ...cuboid.textureOrigin } : null;

    // Capture state for undo BEFORE removal
    const matSnap = {};
    const groupSnap = {};
    const faces = ['top', 'bottom', 'front', 'back', 'left', 'right'];
    const groupManager = this.world.materialGroupManager;
    for (const face of faces) {
      if (cuboid.materials[face]) matSnap[face] = cuboid.materials[face].toJSON();
      if (groupManager) {
        const group = groupManager.getGroupForFace(key, face);
        if (group) groupSnap[face] = group.id;
      }
    }

    // Remove all faces from any groups first
    if (groupManager) {
      for (const face of faces) {
        const group = groupManager.getGroupForFace(key, face);
        if (group) {
          groupManager.removeFacesFromGroup(group.id, [{ cuboidKey: key, face }]);
        }
      }
    }

    // Remove the cuboid from the world
    this.world.removeCuboid(ux, uy, uz, uw, uh, ud);

    // Record undo action
    if (this.undoManager) {
      const world = this.world;
      const editor = this;
      this.undoManager.push({
        description: 'Delete cuboid',
        undo: () => {
          const restored = {};
          for (const [face, data] of Object.entries(matSnap)) restored[face] = Material.fromJSON(data);
          world.addCuboid(ux, uy, uz, uw, uh, ud, restored, texOrigin);
          const gm = world.materialGroupManager;
          if (gm) {
            const groups = new Set();
            for (const [face, groupId] of Object.entries(groupSnap)) {
              gm.assignFacesToGroup(groupId, [{ cuboidKey: key, face }]);
              groups.add(groupId);
            }
            groups.forEach(gid => gm.applyGroupMaterial(gid));
          }
          world.update();
          editor.updateCuboidList();
        },
        redo: () => {
          const gm = world.materialGroupManager;
          if (gm) {
            for (const face of ['top', 'bottom', 'front', 'back', 'left', 'right']) {
              const group = gm.getGroupForFace(key, face);
              if (group) gm.removeFacesFromGroup(group.id, [{ cuboidKey: key, face }]);
            }
          }
          world.removeCuboid(ux, uy, uz, uw, uh, ud);
          world.update();
          editor.updateCuboidList();
        }
      });
    }

    // Clear selection
    this.selectedCuboid = null;
    if (Cuboid.selectedFaces) {
      Cuboid.selectedFaces.clear();
    }
    Cuboid.primarySelectedFace = null;
    Cuboid.selectedFace = null;
    Cuboid.selectionVersion++;

    // Update the world and UI
    this.world.update();
    this.updateCuboidList();
    this.updateSelectionCount();
    this._updateVegetationButton();
  }

  /**
   * Update vegetation delete button visibility based on selected cuboid.
   */
  _updateVegetationButton() {
    if (!this._deleteVegetationBtn) return;
    if (!this.selectedCuboid || !this.world) {
      this._deleteVegetationBtn.style.display = 'none';
      return;
    }
    const impostors = this.world.getImpostorsOnCuboid(this.selectedCuboid);
    this._deleteVegetationBtn.style.display = impostors.length > 0 ? '' : 'none';
    if (impostors.length > 0) {
      this._deleteVegetationBtn.textContent = `Delete Vegetation (${impostors.length})`;
    }
  }

  /**
   * Delete all vegetation on the currently selected cuboid.
   */
  deleteVegetationOnCuboid() {
    if (!this.selectedCuboid) return;
    const impostors = this.world.getImpostorsOnCuboid(this.selectedCuboid);
    if (impostors.length === 0) return;

    // Capture state for undo
    const snapshots = impostors.map(imp => imp.toJSON());

    for (const impostor of impostors) {
      this.world.removeImpostor(impostor.id);
    }

    if (this.undoManager) {
      const world = this.world;
      this.undoManager.push({
        description: 'Delete vegetation on cuboid',
        undo: () => {
          for (const data of snapshots) {
            const imp = new Impostor(data);
            imp._isVegetation = true;
            world.addImpostor(imp);
            imp.sampleAO();
          }
          world.update();
          window.dispatchEvent(new CustomEvent('impostors-changed'));
        },
        redo: () => {
          for (const data of snapshots) {
            world.removeImpostor(data.id);
          }
          world.update();
          window.dispatchEvent(new CustomEvent('impostors-changed'));
        }
      });
    }

    this._updateVegetationButton();
    // Notify impostor tool to refresh its list if present
    window.dispatchEvent(new CustomEvent('impostors-changed'));
  }

  /**
   * Create group management section
   */
  createGroupSection() {
    const section = GUIBuilder.createColorCodedSection('Material Groups', 'success', false);

    // Selection count badge
    this.selectionCountLabel = GUIBuilder.createBadge('0 selected', 'primary');
    section.header.appendChild(this.selectionCountLabel);

    // Group selector
    this.groupSelector = GUIBuilder.createSelect([], '');
    this.groupSelector.addEventListener('change', () => this.onGroupSelectorChange());
    section.body.appendChild(GUIBuilder.createRow('Group', this.groupSelector));

    // Create group button
    const createBtn = GUIBuilder.createButton('Create Group from Selection', 'success', true);
    createBtn.addEventListener('click', () => this.createGroupFromSelection());
    section.body.appendChild(createBtn);

    // Add to group button
    const addBtn = GUIBuilder.createButton('Add Selection to Group', '', true);
    addBtn.addEventListener('click', () => this.addSelectionToGroup());
    section.body.appendChild(addBtn);

    // Remove from group button
    const removeBtn = GUIBuilder.createButton('Remove Selection from Group', 'warning', true);
    removeBtn.addEventListener('click', () => this.removeSelectionFromGroup());
    section.body.appendChild(removeBtn);

    // Group settings subsection (animated, collapsed by default)
    const groupSettings = GUIBuilder.createAnimatedSubsection('Group Settings', 1, true);
    this.groupSettingsContainer = groupSettings.body;
    section.body.appendChild(groupSettings.subsection);

    this.content.appendChild(section.section);
  }

  /**
   * Create material type section with PropertyPanels
   */
  createMaterialSection() {
    const section = GUIBuilder.createColorCodedSection('Material Type', 'primary', false);

    // Material type selector
    this.allMaterialTypes = [
      { value: 'color', label: 'Color' },
      { value: 'texture', label: 'Texture' },
      { value: 'video', label: 'Video' },
      { value: 'gradient', label: 'Gradient' },
      { value: 'pattern', label: 'Pattern' },
      { value: 'text', label: 'Text' },
      { value: 'special', label: 'Special Effects' },
      { value: 'webpage', label: 'Webpage' }
    ];
    // Types excluded from grouped materials (single-face only)
    this.singleFaceOnlyTypes = new Set(['webpage']);

    this.materialTypeSelector = GUIBuilder.createSelect(this.allMaterialTypes, 'color');

    this.materialTypeSelector.addEventListener('change', () => {
      this.updateMaterialProperties();
      // Preview the new material type immediately
      this.previewMaterial();
    });

    section.body.appendChild(GUIBuilder.createRow('Type', this.materialTypeSelector));

    // Properties container
    this.propertiesContainer = document.createElement('div');
    this.propertiesContainer.className = 'material-properties';
    this.propertiesContainer.style.marginTop = '12px';
    section.body.appendChild(this.propertiesContainer);

    // Initialize property panels
    this.initializePropertyPanels();

    this.content.appendChild(section.section);

    // Initial render
    this.updateMaterialProperties();
  }

  /**
   * Create border options section
   */
  createBorderSection() {
    const section = GUIBuilder.createColorCodedSection('Border Options', 'warning', true);

    // Border width slider - range 0-50 for zoom scaling support
    const widthSlider = GUIBuilder.createSlider(0, 50, 0, 0.5, (val) => {
      // Border width changed - preview in real-time
      this.previewMaterial();
    });
    section.body.appendChild(GUIBuilder.createRow('Width', widthSlider.container));
    this.borderWidthSlider = widthSlider.slider;
    this.borderWidthValueInput = widthSlider.valueInput;

    // Border color
    this.borderColorInput = GUIBuilder.createColorPicker('#000000');
    this.borderColorInput.addEventListener('input', () => {
      this.previewMaterial();
    });
    section.body.appendChild(GUIBuilder.createRow('Color', this.borderColorInput));

    this.content.appendChild(section.section);

    // Physics section for collision properties
    this.createPhysicsSection();
  }

  /**
   * Create physics options section (collision properties)
   */
  createPhysicsSection() {
    const section = GUIBuilder.createColorCodedSection('Physics', 'info', true);

    // No Collision checkbox - for water, gas, and other pass-through materials
    const noCollisionCheckbox = GUIBuilder.createCheckbox('No Collision', false);
    noCollisionCheckbox.checkbox.addEventListener('change', () => {
      this.previewMaterial();
    });
    section.body.appendChild(GUIBuilder.createRow('Pass-through', noCollisionCheckbox.container));
    this.noCollisionCheckbox = noCollisionCheckbox.checkbox;

    // Add description text
    const helpText = document.createElement('div');
    helpText.className = 'gui-help-text';
    helpText.textContent = 'Enable for water, gas, or other materials that players can pass through.';
    helpText.style.cssText = 'font-size: 10px; color: #888; margin-top: 4px; padding: 0 4px;';
    section.body.appendChild(helpText);

    this.content.appendChild(section.section);
  }

  /**
   * Initialize PropertyPanel instances
   */
  initializePropertyPanels() {
    this.propertyPanels = {
      color: new ColorPropertyPanel(GUIBuilder, this.propertiesContainer),
      texture: new TexturePropertyPanel(GUIBuilder, this.propertiesContainer),
      gradient: new GradientPropertyPanel(GUIBuilder, this.propertiesContainer),
      pattern: new PatternPropertyPanel(GUIBuilder, this.propertiesContainer),
      video: new VideoPropertyPanel(GUIBuilder, this.propertiesContainer),
      text: new TextPropertyPanel(GUIBuilder, this.propertiesContainer),
      special: new EffectsPropertyPanel(GUIBuilder, this.propertiesContainer),
      webpage: new WebpagePropertyPanel(GUIBuilder, this.propertiesContainer)
    };

    // Set up real-time onChange handlers for all panels
    this.setupRealtimeHandlers();
  }

  /**
   * Set up real-time onChange handlers for PropertyPanels
   * All changes apply as PREVIEW - must click Apply to commit
   */
  setupRealtimeHandlers() {
    // Set generic onChange handler for all panels - use preview mode
    Object.values(this.propertyPanels).forEach(panel => {
      if (panel) {
        panel.onChange = () => this.previewMaterial();
        // Also set specific handlers for backward compatibility
        panel.onAngleChange = () => this.previewMaterial();
        panel.onOpacityChange = () => this.previewMaterial();
        panel.onScaleChange = () => this.previewMaterial();
        panel.onLinkToggle = () => this.previewMaterial();
      }
    });

    // Wire up animated preset handler for texture panel
    if (this.propertyPanels.texture) {
      this.propertyPanels.texture.onAnimatedPreset = (preset) => this.applyAnimatedPreset(preset);
    }
  }

  /**
   * Rebuild material type selector options based on whether the face is in a group.
   * Webpage material is single-face only and excluded from grouped materials.
   * @param {boolean} isGrouped - Whether the current face is in a material group
   */
  updateMaterialTypeOptions(isGrouped) {
    const currentValue = this.materialTypeSelector.value;
    const options = isGrouped
      ? this.allMaterialTypes.filter(t => !this.singleFaceOnlyTypes.has(t.value))
      : this.allMaterialTypes;

    this.materialTypeSelector.innerHTML = '';
    for (const opt of options) {
      const el = document.createElement('option');
      el.value = opt.value;
      el.textContent = opt.label;
      this.materialTypeSelector.appendChild(el);
    }

    // Restore previous selection if still available, otherwise fall back to 'color'
    if (options.some(o => o.value === currentValue)) {
      this.materialTypeSelector.value = currentValue;
    } else {
      this.materialTypeSelector.value = 'color';
      this.updateMaterialProperties();
    }
  }

  /**
   * Update material properties UI based on selected type
   */
  updateMaterialProperties() {
    const type = this.materialTypeSelector.value;

    // Clear container
    this.propertiesContainer.innerHTML = '';

    // Render appropriate panel
    if (this.propertyPanels[type]) {
      this.propertyPanels[type].render();
    }
  }

  /**
   * Set the sidebar manager reference for tab switching
   * @param {SidebarManager} sidebarManager
   */
  /**
   * Set the materials sheet reference for auto-opening on face select
   * @param {TopSheet} sheet
   */
  setMaterialsSheet(sheet) {
    this.materialsSheet = sheet;
  }

  setSidebarManager(sidebarManager) {
    this.sidebarManager = sidebarManager;

    // Listen for tab changes to sync selection when switching to materials tab
    // and revert unapplied previews when switching away
    window.addEventListener('sidebar-tab-change', (e) => {
      if (e.detail.tab === 'materials') {
        this.syncSelectionFromGlobalState();
      } else {
        this.revertToOriginal();
      }
    });
  }

  /**
   * Sync the material editor UI with the current global selection state
   * Called when switching to materials tab to reflect any selection made in edit mode
   */
  syncSelectionFromGlobalState() {
    // Revert any unapplied preview changes before syncing
    this.revertToOriginal();

    if (Cuboid.primarySelectedFace) {
      const separatorIndex = Cuboid.primarySelectedFace.lastIndexOf('|');
      if (separatorIndex !== -1) {
        const cuboidKey = Cuboid.primarySelectedFace.substring(0, separatorIndex);
        const face = Cuboid.primarySelectedFace.substring(separatorIndex + 1);

        // Update UI without switching tabs (we're already on materials tab)
        this.updateCuboidList();
        this.cuboidSelector.value = cuboidKey;
        this.selectedCuboid = this.world.cuboids.get(cuboidKey);
        this.faceSelector.value = face;
        this.selectedFace = face;
        this.updateSelectionCount();
        this.updateGroupSelector();

        // Check if the selected face is in a group
        // MUST run updateMaterialTypeOptions BEFORE updateMaterialTypeSelector so the
        // dropdown has all valid options (e.g. 'webpage') before we try to set the value
        const groupManager = this.world.materialGroupManager;
        if (groupManager) {
          const group = groupManager.getGroupForFace(cuboidKey, face);
          this.updateMaterialTypeOptions(!!group);
          if (group) {
            this.groupSelector.value = group.id;
            this.onGroupSelectorChange();
          } else {
            this.groupSelector.value = '';
            this.groupSettingsContainer.innerHTML = '';
          }
        }

        // Update material properties UI (after options are rebuilt)
        this.updateMaterialTypeSelector();

        // Store original materials for the new selection
        this.storeOriginalMaterials();
      }
    }
  }

  /**
   * Check if the editor is open
   * @returns {boolean} True if editor is visible
   */
  isOpen() {
    // Check if materials tab is active OR if the materials sheet is open
    if (this.materialsSheet && this.materialsSheet.isOpen()) return true;
    return this.sidebarManager && this.sidebarManager.isTabActive('materials');
  }

  /**
   * Handle external face selection (from FacePicker)
   * @param {string} cuboidKey - The cuboid key
   * @param {string} face - The face name
   */
  onFaceSelected(cuboidKey, face) {
    // Revert any unapplied preview changes before changing selection
    this.revertToOriginal();

    // Switch to materials tab if not active
    if (this.sidebarManager && !this.sidebarManager.isTabActive('materials')) {
      this.sidebarManager.switchToTab('materials');
    }
    // Open the materials sheet if available
    if (this.materialsSheet && !this.materialsSheet.isOpen()) {
      this.materialsSheet.open('Material Editor');
    }
    this.updateCuboidList();

    // Update the cuboid selector
    this.cuboidSelector.value = cuboidKey;
    this.selectedCuboid = this.world.cuboids.get(cuboidKey);

    // Update the face selector
    this.faceSelector.value = face;
    this.selectedFace = face;

    // Update group management UI
    this.updateSelectionCount();
    this.updateGroupSelector();

    // Check if the selected face is in a group and update group selector
    // MUST run updateMaterialTypeOptions BEFORE updateMaterialTypeSelector so the
    // dropdown has all valid options (e.g. 'webpage') before we try to set the value
    const groupManager = this.world.materialGroupManager;
    if (groupManager) {
      const group = groupManager.getGroupForFace(cuboidKey, face);
      this.updateMaterialTypeOptions(!!group);
      if (group) {
        this.groupSelector.value = group.id;
        this.onGroupSelectorChange();
      } else {
        this.groupSelector.value = '';
        this.groupSettingsContainer.innerHTML = '';
      }
    }

    // Update the material properties UI (after options are rebuilt)
    this.updateMaterialTypeSelector();

    // Store original materials for the new selection (for future revert)
    this.storeOriginalMaterials();

    // Update vegetation button visibility
    this._updateVegetationButton();
  }

  /**
   * Update the cuboid list dropdown
   */
  updateCuboidList() {
    // Clear existing options
    this.cuboidSelector.innerHTML = '';

    // Add default option
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'Select a cuboid';
    this.cuboidSelector.appendChild(defaultOption);

    // Add option for each cuboid (world.cuboids is a Map)
    Array.from(this.world.cuboids.values()).forEach((cuboid, index) => {
      const option = document.createElement('option');
      option.value = cuboid.getKey(); // Use cuboid key, not index
      option.textContent = `Cuboid ${index + 1} (${cuboid.x}, ${cuboid.y}, ${cuboid.z})`;
      this.cuboidSelector.appendChild(option);
    });

    // Update group list as well
    this.updateGroupList();
  }

  /**
   * Update the group list dropdown
   */
  updateGroupList() {
    this.updateGroupSelector();
    this.updateSelectionCount();
  }

  /**
   * Update the group selector dropdown
   */
  updateGroupSelector() {
    if (!this.groupSelector) return;

    this.groupSelector.innerHTML = '';

    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = 'None (individual faces)';
    this.groupSelector.appendChild(defaultOption);

    const groupManager = this.world.materialGroupManager;
    if (groupManager) {
      const groups = groupManager.getAllGroups();
      groups.forEach(group => {
        const option = document.createElement('option');
        option.value = group.id;
        option.textContent = `${group.name} (${group.size} faces)`;
        this.groupSelector.appendChild(option);
      });
    }
  }

  /**
   * Update selection count display
   */
  updateSelectionCount() {
    const count = Cuboid.selectedFaces ? Cuboid.selectedFaces.size : 0;
    this.selectionCountLabel.textContent = `${count} selected`;
  }

  /**
   * Get selected faces as array
   * @returns {Array} Array of {cuboidKey, face} objects
   */
  getSelectedFacesArray() {
    if (!Cuboid.selectedFaces) return [];
    return Array.from(Cuboid.selectedFaces).map(s => {
      const separatorIndex = s.lastIndexOf('|');
      return {
        cuboidKey: s.substring(0, separatorIndex),
        face: s.substring(separatorIndex + 1)
      };
    });
  }

  /**
   * Handle cuboid selection change
   */
  onCuboidSelected() {
    // Revert any unapplied preview changes before changing selection
    this.revertToOriginal();

    const key = this.cuboidSelector.value;

    if (key) {
      this.selectedCuboid = this.world.cuboids.get(key);
      const serializedFace = `${key}|${this.selectedFace}`;

      // Clear and set single selection
      if (Cuboid.selectedFaces) {
        Cuboid.selectedFaces.clear();
        Cuboid.selectedFaces.add(serializedFace);
      }
      Cuboid.primarySelectedFace = serializedFace;
      Cuboid.selectedFace = {
        cuboidKey: key,
        face: this.selectedFace
      };
      Cuboid.selectionVersion++;

      // Force an update of the world to apply the highlight
      this.world.update();

      // Check if this face is in a group and update available material types
      const groupManager = this.world.materialGroupManager;
      if (groupManager) {
        const isGrouped = !!groupManager.getGroupForFace(key, this.selectedFace);
        this.updateMaterialTypeOptions(isGrouped);
      }

      this.updateMaterialTypeSelector();
      this.updateSelectionCount();
      this.updateGroupSelector();

      // Store original materials for the new selection
      this.storeOriginalMaterials();
    } else {
      // Clear all selections
      if (Cuboid.selectedFaces) {
        Cuboid.selectedFaces.clear();
      }
      Cuboid.primarySelectedFace = null;
      Cuboid.selectedFace = null;
      Cuboid.selectionVersion++;
      this.selectedCuboid = null;
      this.world.update();

      // Clear stored originals when deselecting
      this.originalMaterials.clear();
      this.hasUnappliedChanges = false;
    }

    // Update vegetation button visibility
    this._updateVegetationButton();
  }

  /**
   * Update face selection
   */
  updateFaceSelection() {
    // Revert any unapplied preview changes before changing face
    this.revertToOriginal();

    this.selectedFace = this.faceSelector.value;

    // If we have a selected cuboid, update the selection
    if (this.selectedCuboid) {
      const key = this.selectedCuboid.getKey();
      const serializedFace = `${key}|${this.selectedFace}`;

      if (Cuboid.selectedFaces) {
        Cuboid.selectedFaces.clear();
        Cuboid.selectedFaces.add(serializedFace);
      }
      Cuboid.primarySelectedFace = serializedFace;
      Cuboid.selectedFace = {
        cuboidKey: key,
        face: this.selectedFace
      };
      Cuboid.selectionVersion++;
      this.world.update();

      // Store original materials for the new selection
      this.storeOriginalMaterials();
    }
  }

  /**
   * Update material type selector based on current face material
   */
  updateMaterialTypeSelector() {
    if (!this.selectedCuboid) return;

    const material = this.selectedCuboid.materials[this.selectedFace];
    if (material && material.type) {
      this.materialTypeSelector.value = material.type === 'special' ? 'special' : material.type;
      this.updateMaterialProperties();

      // Populate the property panel with current material values
      this.setPropertyValues(material);

      // Set border values - update both slider and value input
      const borderWidth = material.borderWidth !== undefined ? material.borderWidth : 0;
      if (this.borderWidthSlider) {
        this.borderWidthSlider.value = borderWidth;
      }
      if (this.borderWidthValueInput) {
        this.borderWidthValueInput.value = borderWidth;
      }
      if (this.borderColorInput) {
        let borderColor = material.borderColor || '#000000';
        // Convert rgba to hex if needed
        if (borderColor.startsWith && borderColor.startsWith('rgba')) {
          borderColor = this.rgbaToHex(borderColor);
        }
        this.borderColorInput.value = borderColor;
      }

      // Set noCollision checkbox
      if (this.noCollisionCheckbox) {
        this.noCollisionCheckbox.checked = material.noCollision || false;
      }
    }
  }

  /**
   * Set property values in the current PropertyPanel
   * @param {Material} material - The material to load values from
   */
  setPropertyValues(material) {
    const type = this.materialTypeSelector.value;
    const panel = this.propertyPanels[type];

    if (!panel) return;

    // Convert material to data format for the panel
    const data = { ...material };

    // Handle gradient nested structure
    if (data.gradient) {
      data.gradientType = data.gradient.type;
      data.color1 = data.gradient.colors ? data.gradient.colors[0] : '#3498db';
      data.color2 = data.gradient.colors ? data.gradient.colors[1] : '#e74c3c';
      data.angle = data.gradient.direction || 45;
    }

    // Handle pattern nested structure
    if (data.pattern) {
      data.patternType = data.pattern.type;
      // Copy all pattern properties to data for panel setData
      if (data.pattern.colors) {
        data.color1 = data.pattern.colors[0];
        data.color2 = data.pattern.colors[1];
      }
      if (data.pattern.direction) data.direction = data.pattern.direction;
      if (data.pattern.size) data.size = data.pattern.size;
      if (data.pattern.rotation !== undefined) data.rotation = data.pattern.rotation;
      if (data.pattern.backgroundColor) data.backgroundColor = data.pattern.backgroundColor;
      if (data.pattern.dotColor) data.dotColor = data.pattern.dotColor;
      if (data.pattern.dotSize) data.dotSize = data.pattern.dotSize;
      if (data.pattern.spacing) data.spacing = data.pattern.spacing;
      if (data.pattern.gridColor) data.gridColor = data.pattern.gridColor;
      if (data.pattern.gridSize) data.gridSize = data.pattern.gridSize;
      if (data.pattern.lineWidth) data.lineWidth = data.pattern.lineWidth;
    }

    // Handle texture - the texture URL is stored in 'texture' property
    if (data.texture) {
      data.url = data.texture;
    }

    // Handle video - the video URL is stored in 'video' property
    if (data.video) {
      data.url = data.video;
    }

    // Handle webpage - the webpage URL is stored in 'webpage' property
    if (data.webpage) {
      data.url = data.webpage;
      data.interactive = data.webpageInteractive;
    }

    // Handle text properties
    if (data.text) {
      data.fontSize = parseInt(data.textSize) || 16;
    }

    // Convert any rgba colors to hex for color pickers
    if (data.color && data.color.startsWith && data.color.startsWith('rgba')) {
      data.color = this.rgbaToHex(data.color);
    }
    if (data.color1 && data.color1.startsWith && data.color1.startsWith('rgba')) {
      data.color1 = this.rgbaToHex(data.color1);
    }
    if (data.color2 && data.color2.startsWith && data.color2.startsWith('rgba')) {
      data.color2 = this.rgbaToHex(data.color2);
    }
    if (data.textColor && data.textColor.startsWith && data.textColor.startsWith('rgba')) {
      data.textColor = this.rgbaToHex(data.textColor);
    }
    if (data.bgColor && data.bgColor.startsWith && data.bgColor.startsWith('rgba')) {
      data.bgColor = this.rgbaToHex(data.bgColor);
    }

    // Call setData on the panel to populate UI
    panel.setData(data);
  }

  /**
   * Convert rgba color to hex
   * @param {string} rgba - rgba color string
   * @returns {string} hex color string
   */
  rgbaToHex(rgba) {
    // Extract rgba values
    const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!match) return '#000000';

    const r = parseInt(match[1]);
    const g = parseInt(match[2]);
    const b = parseInt(match[3]);

    // Convert to hex
    return '#' + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  }

  /**
   * Handle group selector change
   */
  onGroupSelectorChange() {
    const groupId = this.groupSelector.value;
    const groupManager = this.world.materialGroupManager;

    if (!groupId || !groupManager) {
      this.groupSettingsContainer.innerHTML = '';
      return;
    }

    const group = groupManager.getGroup(groupId);
    if (group) {
      this.renderGroupSettings(group);
    }
  }

  /**
   * Select a group and highlight all its faces
   * @param {string} groupId - The group ID to select
   */
  selectGroup(groupId) {
    const groupManager = this.world.materialGroupManager;
    if (!groupManager) return;

    const group = groupManager.getGroup(groupId);
    if (!group) return;

    // Update the dropdown
    this.groupSelector.value = groupId;
    this.onGroupSelectorChange();

    // Clear current selection and select all faces in the group
    if (!Cuboid.selectedFaces) {
      Cuboid.selectedFaces = new Set();
    }
    Cuboid.selectedFaces.clear();

    // Add all faces from the group to the selection
    for (const serializedFace of group.faces) {
      Cuboid.selectedFaces.add(serializedFace);
    }

    // Set the primary selected face to the first face in the group
    if (group.faces.size > 0) {
      Cuboid.primarySelectedFace = Array.from(group.faces)[0];
    }

    // Update selection count display
    this.updateSelectionCount();

    // Force world update to apply highlighting
    this.world.cuboids.forEach(cuboid => {
      cuboid.needsUpdate = true;
    });
    this.world.update();
  }

  /**
   * Render group settings UI
   */
  renderGroupSettings(group) {
    this.groupSettingsContainer.innerHTML = '';

    // Group name
    const nameInput = GUIBuilder.createInput(group.name || '', 'text');
    nameInput.addEventListener('change', () => {
      group.name = nameInput.value;
      this.updateGroupSelector(); // Update dropdown to show new name
    });
    this.groupSettingsContainer.appendChild(GUIBuilder.createRow('Name', nameInput));

    // Face count (use group.size, not group.faces.length - Set has .size not .length)
    const faceCount = GUIBuilder.createBadge(`${group.size} faces`, 'info');
    this.groupSettingsContainer.appendChild(GUIBuilder.createRow('Faces', faceCount));

    // Validate & Cleanup button - helps fix corrupted group data
    const cleanupBtn = GUIBuilder.createButton('Validate & Cleanup', 'info', true);
    cleanupBtn.style.marginTop = '4px';
    cleanupBtn.addEventListener('click', () => this.validateAndCleanupGroups());
    this.groupSettingsContainer.appendChild(cleanupBtn);

    // Delete group button
    const deleteBtn = GUIBuilder.createButton('Delete Group', 'danger', true);
    deleteBtn.style.marginTop = '4px';
    deleteBtn.addEventListener('click', () => {
      if (confirm('Delete this group?')) {
        // Capture group state for undo
        const gm = this.world.materialGroupManager;
        const groupId = group.id;
        const groupName = group.name || '';
        const groupFaces = group.faces ? [...group.faces].map(sf => {
          const si = sf.lastIndexOf('|');
          return { cuboidKey: sf.substring(0, si), face: sf.substring(si + 1) };
        }) : [];
        const groupMat = group.material ? JSON.parse(JSON.stringify(group.material)) : null;

        gm.deleteGroup(groupId);
        this.updateGroupList();
        this.groupSettingsContainer.innerHTML = '';

        if (this.undoManager) {
          const world = this.world;
          const editor = this;
          this.undoManager.push({
            description: 'Delete group',
            undo: () => {
              const gm = world.materialGroupManager;
              const newGroup = gm.createGroup({ name: groupName });
              // Recreate with same ID by manipulating directly
              gm.groups.delete(newGroup.id);
              newGroup.id = groupId;
              gm.groups.set(groupId, newGroup);
              if (groupFaces.length > 0) gm.assignFacesToGroup(groupId, groupFaces);
              if (groupMat) {
                newGroup.material = Material.fromJSON(groupMat);
                gm.applyGroupMaterial(groupId);
              }
              world.update();
              editor.updateGroupList();
            },
            redo: () => {
              const gm = world.materialGroupManager;
              gm.deleteGroup(groupId);
              world.update();
              editor.updateGroupList();
            }
          });
        }
      }
    });
    this.groupSettingsContainer.appendChild(deleteBtn);
  }

  /**
   * Validate and cleanup all material groups
   * Removes stale references and fixes duplicates
   */
  validateAndCleanupGroups() {
    const groupManager = this.world.materialGroupManager;
    const stats = groupManager.validateAndCleanup();

    const message = `Validation complete:\n` +
      `- Groups checked: ${stats.groupsChecked}\n` +
      `- Faces removed: ${stats.facesRemoved}\n` +
      `- Duplicates fixed: ${stats.duplicatesFixed}`;

    alert(message);

    // Refresh the UI
    this.updateGroupList();
    const currentGroupId = this.groupSelector.value;
    if (currentGroupId) {
      const group = groupManager.getGroup(currentGroupId);
      if (group) {
        this.renderGroupSettings(group);
      } else {
        this.groupSelector.value = '';
        this.groupSettingsContainer.innerHTML = '';
      }
    }
  }

  /**
   * Create a group from selected faces
   */
  createGroupFromSelection() {
    const selectedFaces = this.getSelectedFacesArray();

    if (selectedFaces.length === 0) {
      alert('Please select faces first (Shift+Click on faces)');
      return;
    }

    // Filter to only include valid parent cuboid keys (not fragment keys)
    const validFaces = this.filterToParentKeys(selectedFaces);
    if (validFaces.length === 0) {
      alert('No valid faces selected');
      return;
    }

    const groupName = prompt('Enter group name:', `Group ${this.world.materialGroupManager.groups.size + 1}`);
    if (!groupName) return;

    // Create the group with the name option
    const group = this.world.materialGroupManager.createGroup({ name: groupName });
    // Assign selected faces to the group
    this.world.materialGroupManager.assignFacesToGroup(group.id, validFaces);
    this.updateGroupList();

    // Update group selector to show the new group
    this.groupSelector.value = group.id;
    this.renderGroupSettings(group);

    // Face is now in a group - update material type options to exclude single-face-only types
    this.updateMaterialTypeOptions(true);

    // Record undo action
    if (this.undoManager) {
      const groupId = group.id;
      const faces = validFaces.map(f => ({ ...f }));
      const world = this.world;
      const editor = this;
      this.undoManager.push({
        description: 'Create group',
        undo: () => {
          world.materialGroupManager.deleteGroup(groupId);
          world.update();
          editor.updateGroupList();
        },
        redo: () => {
          const gm = world.materialGroupManager;
          const g = gm.createGroup({ name: groupName });
          gm.groups.delete(g.id);
          g.id = groupId;
          gm.groups.set(groupId, g);
          gm.assignFacesToGroup(groupId, faces);
          world.update();
          editor.updateGroupList();
        }
      });
    }

    // Trigger highlight refresh - selected faces stay red, other group faces (if any) show blue
    Cuboid.selectionVersion++;
    this.world.update();
  }

  /**
   * Add selected faces to existing group
   */
  addSelectionToGroup() {
    const groupId = this.groupSelector.value;
    if (!groupId) {
      alert('Please select a group first');
      return;
    }

    const selectedFaces = this.getSelectedFacesArray();
    if (selectedFaces.length === 0) {
      alert('Please select faces first (Shift+Click on faces)');
      return;
    }

    // Filter to only include valid parent cuboid keys (not fragment keys)
    const validFaces = this.filterToParentKeys(selectedFaces);
    if (validFaces.length === 0) {
      alert('No valid faces to add');
      return;
    }

    this.world.materialGroupManager.assignFacesToGroup(groupId, validFaces);
    this.updateGroupList();

    // Face is now in a group - update material type options to exclude single-face-only types
    this.updateMaterialTypeOptions(true);

    // Record undo action
    if (this.undoManager) {
      const faces = validFaces.map(f => ({ ...f }));
      const gId = groupId;
      const world = this.world;
      const editor = this;
      this.undoManager.push({
        description: 'Add faces to group',
        undo: () => {
          world.materialGroupManager.removeFacesFromGroup(gId, faces);
          world.update();
          editor.updateGroupList();
        },
        redo: () => {
          world.materialGroupManager.assignFacesToGroup(gId, faces);
          world.update();
          editor.updateGroupList();
        }
      });
    }

    // Trigger highlight refresh - selection stays the same, but group membership changed
    // This will show selected faces in red and other group members in blue
    Cuboid.selectionVersion++;
    this.world.update();

    // Update group settings display if this group is selected
    if (this.groupSelector.value === groupId) {
      const group = this.world.materialGroupManager.getGroup(groupId);
      if (group) {
        this.renderGroupSettings(group);
      }
    }
  }

  /**
   * Remove selected faces from their current group
   */
  removeSelectionFromGroup() {
    const selectedFaces = this.getSelectedFacesArray();
    if (selectedFaces.length === 0) {
      alert('Please select faces first (Shift+Click on faces)');
      return;
    }

    // Filter to only include valid parent cuboid keys
    const validFaces = this.filterToParentKeys(selectedFaces);
    if (validFaces.length === 0) {
      alert('No valid faces to remove');
      return;
    }

    const groupManager = this.world.materialGroupManager;
    let removedCount = 0;

    // Capture group assignments before removal for undo
    const removedAssignments = [];
    for (const { cuboidKey, face } of validFaces) {
      const group = groupManager.getGroupForFace(cuboidKey, face);
      if (group) {
        removedAssignments.push({ cuboidKey, face, groupId: group.id });
      }
    }

    // Remove each face from whatever group it's in
    for (const { cuboidKey, face } of validFaces) {
      const group = groupManager.getGroupForFace(cuboidKey, face);
      if (group) {
        groupManager.removeFacesFromGroup(group.id, [{ cuboidKey, face }]);
        removedCount++;
      }
    }

    if (removedCount === 0) {
      alert('Selected faces are not in any group');
      return;
    }

    // Record undo action
    if (this.undoManager && removedAssignments.length > 0) {
      const world = this.world;
      const editor = this;
      const assignments = removedAssignments.map(a => ({ ...a }));
      this.undoManager.push({
        description: 'Remove faces from group',
        undo: () => {
          const gm = world.materialGroupManager;
          const groups = new Set();
          for (const { cuboidKey, face, groupId } of assignments) {
            gm.assignFacesToGroup(groupId, [{ cuboidKey, face }]);
            groups.add(groupId);
          }
          groups.forEach(gid => gm.applyGroupMaterial(gid));
          world.update();
          editor.updateGroupList();
        },
        redo: () => {
          const gm = world.materialGroupManager;
          for (const { cuboidKey, face, groupId } of assignments) {
            gm.removeFacesFromGroup(groupId, [{ cuboidKey, face }]);
          }
          world.update();
          editor.updateGroupList();
        }
      });
    }

    this.updateGroupList();
    this.updateSelectionCount();

    // Face may no longer be in a group - check and restore full material type options
    if (this.selectedCuboid) {
      const cuboidKey = this.selectedCuboid.getKey();
      const stillGrouped = !!groupManager.getGroupForFace(cuboidKey, this.selectedFace);
      this.updateMaterialTypeOptions(stillGrouped);
    }

    // Trigger highlight refresh - faces are no longer in groups so blue highlight should be removed
    Cuboid.selectionVersion++;
    this.world.update();

    // Clear group selector if the current group is now empty or faces were removed from it
    const currentGroupId = this.groupSelector.value;
    if (currentGroupId) {
      const currentGroup = groupManager.getGroup(currentGroupId);
      if (!currentGroup || currentGroup.size === 0) {
        this.groupSelector.value = '';
        this.groupSettingsContainer.innerHTML = '';
      } else {
        this.onGroupSelectorChange(); // Refresh group settings display
      }
    }
  }

  /**
   * Filter faces to only include valid parent cuboid keys (exclude fragments)
   * @param {Array} faces - Array of { cuboidKey, face } objects
   * @returns {Array} Filtered array with only parent cuboid keys
   */
  filterToParentKeys(faces) {
    return faces.filter(({ cuboidKey }) => {
      // Check if this key exists in the main cuboids map (not fragments)
      if (this.world.cuboids.has(cuboidKey)) {
        return true;
      }
      // If not found, it might be a fragment key - look up the parent
      // Fragment keys have the same format but aren't in world.cuboids
      Debug.warn('MaterialEditor', `Filtering out non-parent key: ${cuboidKey}`);
      return false;
    });
  }

  /**
   * Store original materials for currently selected faces
   * Called when selection changes to enable revert functionality
   */
  storeOriginalMaterials() {
    // Clear any existing stored originals
    this.originalMaterials.clear();
    this.originalGroupMaterials = new Map(); // Store group materials separately
    this.hasUnappliedChanges = false;

    if (!Cuboid.selectedFaces || Cuboid.selectedFaces.size === 0) return;

    const groupManager = this.world.materialGroupManager;

    // Store original material for each selected face
    for (const serializedFace of Cuboid.selectedFaces) {
      const separatorIndex = serializedFace.lastIndexOf('|');
      const cuboidKey = serializedFace.substring(0, separatorIndex);
      const face = serializedFace.substring(separatorIndex + 1);

      const cuboid = this.world.cuboids.get(cuboidKey);
      if (cuboid && cuboid.materials[face]) {
        // Deep copy the material to preserve original state
        const originalMaterial = JSON.parse(JSON.stringify(cuboid.materials[face]));
        this.originalMaterials.set(serializedFace, originalMaterial);

        // Also store group material if face is in a group
        const faceGroup = groupManager?.getGroupForFace(cuboidKey, face);
        if (faceGroup && faceGroup.material && !this.originalGroupMaterials.has(faceGroup.id)) {
          // Deep copy the group material
          const originalGroupMaterial = JSON.parse(JSON.stringify(faceGroup.material));
          this.originalGroupMaterials.set(faceGroup.id, originalGroupMaterial);
        }
      }
    }
  }

  /**
   * Revert all selected faces to their original materials
   * Called when selection changes without applying
   */
  revertToOriginal() {
    if (this.originalMaterials.size === 0 || !this.hasUnappliedChanges) return;

    Debug.log('MaterialEditor', 'revertToOriginal: Reverting', this.originalMaterials.size, 'faces');

    const groupManager = this.world.materialGroupManager;

    // First, revert group materials (this is important for faces in groups)
    if (this.originalGroupMaterials && this.originalGroupMaterials.size > 0) {
      for (const [groupId, originalGroupMaterialData] of this.originalGroupMaterials) {
        const group = groupManager?.getGroup(groupId);
        if (group) {
          // Clean up current material's audio source before reverting
          if (group.material && group.material._cleanupAudioSource) {
            group.material._cleanupAudioSource();
          }
          // Recreate Material instance from stored data
          const material = this.recreateMaterialFromData(originalGroupMaterialData);
          group.material = material;
          // Re-apply group material to all faces in the group
          groupManager.applyGroupMaterial(groupId);
        }
      }
    }

    // Then revert individual materials for faces not in groups
    const cuboidUpdates = new Map(); // cuboidKey -> { cuboid, materials: { face: material } }

    for (const [serializedFace, originalMaterialData] of this.originalMaterials) {
      const separatorIndex = serializedFace.lastIndexOf('|');
      const cuboidKey = serializedFace.substring(0, separatorIndex);
      const face = serializedFace.substring(separatorIndex + 1);

      // Skip faces that are in groups (they were already reverted above)
      const faceGroup = groupManager?.getGroupForFace(cuboidKey, face);
      if (faceGroup) continue;

      const cuboid = this.world.cuboids.get(cuboidKey);
      if (cuboid) {
        if (!cuboidUpdates.has(cuboidKey)) {
          cuboidUpdates.set(cuboidKey, { cuboid, materials: {} });
        }
        // Recreate Material instance from stored data
        const material = this.recreateMaterialFromData(originalMaterialData);
        cuboidUpdates.get(cuboidKey).materials[face] = material;
      }
    }

    // Apply reverted materials for non-group faces
    for (const [cuboidKey, { cuboid, materials }] of cuboidUpdates) {
      this.world.updateCuboidMaterials(
        cuboid.x,
        cuboid.y,
        cuboid.z,
        cuboid.width,
        cuboid.height,
        cuboid.depth,
        materials
      );
    }

    // Force world update
    this.world.update();

    // Clear the stored originals
    this.originalMaterials.clear();
    if (this.originalGroupMaterials) this.originalGroupMaterials.clear();
    this.hasUnappliedChanges = false;
  }

  /**
   * Preview material changes without committing
   * Applies visually but can be reverted if selection changes
   */
  previewMaterial() {
    if (!this.selectedCuboid) return;

    // Store originals on first preview if not already stored
    if (this.originalMaterials.size === 0) {
      this.storeOriginalMaterials();
    }

    const materialType = this.materialTypeSelector.value;
    let material;

    // Get data from appropriate PropertyPanel
    const panel = this.propertyPanels[materialType];
    if (panel) {
      const data = panel.getData();
      Debug.log('MaterialEditor', 'previewMaterial: Panel data:', {
        type: data.type,
        scaleX: data.scaleX,
        scaleY: data.scaleY,
        scaleZ: data.scaleZ,
        opacity: data.opacity
      });
      material = this.createMaterialFromData(data);
      Debug.log('MaterialEditor', 'previewMaterial: Created material:', {
        type: material.type,
        scaleX: material.scaleX,
        scaleY: material.scaleY,
        scaleZ: material.scaleZ,
        opacity: material.opacity
      });
    }

    if (material) {
      // Apply common border properties
      const borderWidth = parseFloat(this.borderWidthSlider.value);
      const borderColor = this.borderColorInput.value;
      const noCollision = this.noCollisionCheckbox ? this.noCollisionCheckbox.checked : false;

      material.borderWidth = borderWidth;
      material.borderColor = borderColor;
      // Don't apply noCollision during preview — only visual changes should preview
      // noCollision is applied on final commit in applyMaterial()

      // Preview on all selected faces
      const groupManager = this.world.materialGroupManager;
      const allSelected = Cuboid.selectedFaces ? Array.from(Cuboid.selectedFaces) : [];
      const appliedGroups = new Set();

      const facesToPreview = allSelected.length > 0 ? allSelected : [`${this.selectedCuboid.getKey()}|${this.selectedFace}`];

      for (const serialized of facesToPreview) {
        const sepIdx = serialized.lastIndexOf('|');
        const cuboidKey = serialized.substring(0, sepIdx);
        const face = serialized.substring(sepIdx + 1);
        const cuboid = this.world.cuboids.get(cuboidKey);
        if (!cuboid) continue;

        const faceMaterial = this.createMaterialFromData(panel.getData());
        faceMaterial.borderWidth = borderWidth;
        faceMaterial.borderColor = borderColor;

        const faceGroup = groupManager?.getGroupForFace(cuboidKey, face);

        if (faceGroup) {
          if (appliedGroups.has(faceGroup.id)) continue;
          appliedGroups.add(faceGroup.id);

          if (faceGroup.material && faceGroup.material._cleanupAudioSource) {
            faceGroup.material._cleanupAudioSource();
          }
          faceGroup.material = faceMaterial;
          this.syncMaterialToGroupProperties(faceGroup, faceMaterial);
          groupManager.applyGroupMaterial(faceGroup.id);
        } else {
          this.world.updateCuboidMaterials(
            cuboid.x, cuboid.y, cuboid.z,
            cuboid.width, cuboid.height, cuboid.depth,
            { [face]: faceMaterial }
          );
        }
      }

      this.world.update();

      // Mark as having unapplied changes
      this.hasUnappliedChanges = true;
    }
  }

  /**
   * Apply material to selected face or group (COMMIT changes)
   * Clears original materials since changes are now permanent
   */
  applyMaterial() {
    Debug.log('MaterialEditor', 'applyMaterial: Committing changes');

    if (!this.selectedCuboid) {
      alert('Please select a cuboid first');
      return;
    }

    // Ensure originals are stored for undo (may be empty if user applied without previewing)
    if (this.originalMaterials.size === 0) {
      this.storeOriginalMaterials();
    }

    const materialType = this.materialTypeSelector.value;
    let material;

    // Get data from appropriate PropertyPanel
    const panel = this.propertyPanels[materialType];
    if (panel) {
      const data = panel.getData();
      Debug.log('MaterialEditor', 'applyMaterial: Panel data:', data);
      material = this.createMaterialFromData(data);
      Debug.log('MaterialEditor', 'applyMaterial: Created material:', material);
    }

    if (material) {
      // Apply common border properties
      const borderWidth = parseFloat(this.borderWidthSlider.value);
      const borderColor = this.borderColorInput.value;
      const noCollision = this.noCollisionCheckbox ? this.noCollisionCheckbox.checked : false;

      material.borderWidth = borderWidth;
      material.borderColor = borderColor;
      material.noCollision = noCollision;

      // Snapshot before-state for undo (from stored originals)
      const beforeFaces = new Map(this.originalMaterials); // serializedFace -> materialJSON
      const beforeGroups = this.originalGroupMaterials ? new Map(this.originalGroupMaterials) : new Map();

      // Collect all selected faces to apply to
      const groupManager = this.world.materialGroupManager;
      const allSelected = Cuboid.selectedFaces ? Array.from(Cuboid.selectedFaces) : [];
      const appliedGroups = new Set(); // Track groups already applied to avoid duplicates

      // If no multi-selection, fall back to primary face only
      const facesToApply = allSelected.length > 0 ? allSelected : [`${this.selectedCuboid.getKey()}|${this.selectedFace}`];

      for (const serialized of facesToApply) {
        const sepIdx = serialized.lastIndexOf('|');
        const cuboidKey = serialized.substring(0, sepIdx);
        const face = serialized.substring(sepIdx + 1);
        const cuboid = this.world.cuboids.get(cuboidKey);
        if (!cuboid) continue;

        // Create a fresh material instance for each face
        const faceMaterial = this.createMaterialFromData(panel.getData());
        faceMaterial.borderWidth = borderWidth;
        faceMaterial.borderColor = borderColor;
        faceMaterial.noCollision = noCollision;

        const faceGroup = groupManager?.getGroupForFace(cuboidKey, face);

        if (faceGroup) {
          // Only apply to each group once
          if (appliedGroups.has(faceGroup.id)) continue;
          appliedGroups.add(faceGroup.id);

          if (faceGroup.material && faceGroup.material._cleanupAudioSource) {
            faceGroup.material._cleanupAudioSource();
          }
          faceGroup.material = faceMaterial;
          this.syncMaterialToGroupProperties(faceGroup, faceMaterial);
          groupManager.applyGroupMaterial(faceGroup.id);
        } else {
          this.world.updateCuboidMaterials(
            cuboid.x, cuboid.y, cuboid.z,
            cuboid.width, cuboid.height, cuboid.depth,
            { [face]: faceMaterial }
          );
        }
      }

      // Snapshot after-state for redo
      const afterFaces = new Map();
      const afterGroups = new Map();
      const afterGroupsProcessed = new Set();
      for (const serialized of facesToApply) {
        const sepIdx = serialized.lastIndexOf('|');
        const cuboidKey = serialized.substring(0, sepIdx);
        const face = serialized.substring(sepIdx + 1);
        const cuboid = this.world.cuboids.get(cuboidKey);
        if (!cuboid || !cuboid.materials[face]) continue;
        afterFaces.set(serialized, JSON.parse(JSON.stringify(cuboid.materials[face])));
        const faceGroup = groupManager?.getGroupForFace(cuboidKey, face);
        if (faceGroup && !afterGroupsProcessed.has(faceGroup.id)) {
          afterGroupsProcessed.add(faceGroup.id);
          if (faceGroup.material) afterGroups.set(faceGroup.id, JSON.parse(JSON.stringify(faceGroup.material)));
        }
      }

      // Record undo action
      if (this.undoManager && beforeFaces.size > 0) {
        const world = this.world;
        const editor = this;
        this.undoManager.push({
          description: 'Apply material',
          undo: () => {
            const gm = world.materialGroupManager;
            // Revert group materials
            const revertedGroups = new Set();
            if (gm) {
              for (const [groupId, matData] of beforeGroups) {
                const group = gm.getGroup(groupId);
                if (group) {
                  if (group.material && group.material._cleanupAudioSource) group.material._cleanupAudioSource();
                  group.material = Material.fromJSON(matData);
                  editor.syncMaterialToGroupProperties(group, group.material);
                  gm.applyGroupMaterial(groupId);
                  revertedGroups.add(groupId);
                }
              }
            }
            // Revert individual face materials
            const cuboidUpdates = new Map();
            for (const [serialized, matData] of beforeFaces) {
              const sepIdx = serialized.lastIndexOf('|');
              const cuboidKey = serialized.substring(0, sepIdx);
              const face = serialized.substring(sepIdx + 1);
              const faceGroup = gm?.getGroupForFace(cuboidKey, face);
              if (faceGroup && revertedGroups.has(faceGroup.id)) continue;
              const cuboid = world.cuboids.get(cuboidKey);
              if (cuboid) {
                if (!cuboidUpdates.has(cuboidKey)) cuboidUpdates.set(cuboidKey, { cuboid, materials: {} });
                cuboidUpdates.get(cuboidKey).materials[face] = Material.fromJSON(matData);
              }
            }
            for (const [, { cuboid, materials }] of cuboidUpdates) {
              world.updateCuboidMaterials(cuboid.x, cuboid.y, cuboid.z, cuboid.width, cuboid.height, cuboid.depth, materials);
            }
            world.update();
          },
          redo: () => {
            const gm = world.materialGroupManager;
            const redoneGroups = new Set();
            if (gm) {
              for (const [groupId, matData] of afterGroups) {
                const group = gm.getGroup(groupId);
                if (group) {
                  if (group.material && group.material._cleanupAudioSource) group.material._cleanupAudioSource();
                  group.material = Material.fromJSON(matData);
                  editor.syncMaterialToGroupProperties(group, group.material);
                  gm.applyGroupMaterial(groupId);
                  redoneGroups.add(groupId);
                }
              }
            }
            const cuboidUpdates = new Map();
            for (const [serialized, matData] of afterFaces) {
              const sepIdx = serialized.lastIndexOf('|');
              const cuboidKey = serialized.substring(0, sepIdx);
              const face = serialized.substring(sepIdx + 1);
              const faceGroup = gm?.getGroupForFace(cuboidKey, face);
              if (faceGroup && redoneGroups.has(faceGroup.id)) continue;
              const cuboid = world.cuboids.get(cuboidKey);
              if (cuboid) {
                if (!cuboidUpdates.has(cuboidKey)) cuboidUpdates.set(cuboidKey, { cuboid, materials: {} });
                cuboidUpdates.get(cuboidKey).materials[face] = Material.fromJSON(matData);
              }
            }
            for (const [, { cuboid, materials }] of cuboidUpdates) {
              world.updateCuboidMaterials(cuboid.x, cuboid.y, cuboid.z, cuboid.width, cuboid.height, cuboid.depth, materials);
            }
            world.update();
          }
        });
      }

      this.onGroupSelectorChange();
      this.world.update();

      // If collision was re-enabled, push any characters out of solid cuboids
      if (!noCollision && this.world.collisionSystem && this.world.characters) {
        for (const character of this.world.characters.values()) {
          this.world.collisionSystem.resolveOverlaps(character);
        }
      }

      // Changes are now committed - clear the stored originals
      this.originalMaterials.clear();
      this.hasUnappliedChanges = false;
      Debug.log('MaterialEditor', 'applyMaterial: Applied to', facesToApply.length, 'face(s)');
    }
  }

  /**
   * Apply an animated texture preset (water/lava) to the selected cuboid.
   * Populates the texture panel with preset values and triggers normal apply flow.
   * @param {Object} preset - Preset with texture, opacity, noCollision
   */
  applyAnimatedPreset(preset) {
    if (!this.selectedCuboid) {
      alert('Please select a cuboid first');
      return;
    }

    // Populate the texture panel with preset values (panel is already visible since
    // the animated preset buttons are inside the texture panel)
    const panel = this.propertyPanels.texture;
    if (panel) {
      panel.setData({ url: preset.texture, opacity: preset.opacity });
    }

    // Set noCollision
    if (this.noCollisionCheckbox) {
      this.noCollisionCheckbox.checked = preset.noCollision;
    }

    // Use the normal apply flow (handles undo, groups, preview, etc.)
    this.applyMaterial();

    Debug.log('MaterialEditor', 'applyAnimatedPreset: Applied', preset.id);
  }

  /**
   * Sync material properties to group properties for triplanar projection
   * @param {MaterialGroup} group - The material group
   * @param {Material} material - The material with properties to sync
   */
  syncMaterialToGroupProperties(group, material) {
    Debug.log('MaterialEditor', 'syncMaterialToGroupProperties: Syncing material to group:', { material, group });

    // Sync scale properties
    if (material.scaleX !== undefined || material.scaleY !== undefined || material.scaleZ !== undefined) {
      const scaleX = material.scaleX || 1;
      const scaleY = material.scaleY || 1;
      const scaleZ = material.scaleZ || 1;
      const scale = { x: scaleX, y: scaleY, z: scaleZ };

      switch (material.type) {
        case 'texture':
          group.textureScale = scale;
          break;
        case 'video':
          group.videoScale = scale;
          break;
        case 'gradient':
          group.gradientScale = scale;
          break;
        case 'pattern':
          group.patternScale = scale;
          break;
        case 'special':
          group.effectScale = scale;
          break;
        case 'text':
          group.textScale = scale;
          break;
      }
      Debug.log('MaterialEditor', 'syncMaterialToGroupProperties: Set scale:', scale);
    }

    // Sync offset properties
    if (material.offsetX !== undefined || material.offsetY !== undefined || material.offsetZ !== undefined) {
      const offsetX = material.offsetX || 0;
      const offsetY = material.offsetY || 0;
      const offsetZ = material.offsetZ || 0;
      const offset = { x: offsetX, y: offsetY, z: offsetZ };

      switch (material.type) {
        case 'texture':
          group.textureOffset = offset;
          break;
        case 'video':
          group.videoOffset = offset;
          break;
        case 'gradient':
          group.gradientOffset = offset;
          break;
        case 'pattern':
          group.patternOffset = offset;
          break;
        case 'special':
          group.effectOffset = offset;
          break;
        case 'text':
          group.textOffset = offset;
          break;
      }
      Debug.log('MaterialEditor', 'syncMaterialToGroupProperties: Set offset:', offset);
    }

    // Sync rotation for patterns
    if (material.rotation !== undefined) {
      if (material.type === 'pattern') {
        group.patternRotation = material.rotation;
        Debug.log('MaterialEditor', 'syncMaterialToGroupProperties: Set pattern rotation:', material.rotation);
      } else if (material.type === 'special') {
        group.effectRotation = material.rotation;
        Debug.log('MaterialEditor', 'syncMaterialToGroupProperties: Set effect rotation:', material.rotation);
      }
    }

    // Sync gradient axis and angle
    if (material.type === 'gradient') {
      if (material.axis !== undefined) {
        group.gradientAxis = material.axis;
        Debug.log('MaterialEditor', 'syncMaterialToGroupProperties: Set gradient axis:', material.axis);
      }
      // Sync gradient angle (rotation)
      if (material.gradient && material.gradient.direction !== undefined) {
        group.gradientAngle = material.gradient.direction;
        Debug.log('MaterialEditor', 'syncMaterialToGroupProperties: Set gradient angle:', material.gradient.direction);
      }
    }

    // Sync text axis
    if (material.axis !== undefined && material.type === 'text') {
      group.textAxis = material.axis;
      Debug.log('MaterialEditor', 'syncMaterialToGroupProperties: Set text axis:', material.axis);
    }

    // Sync opacity
    if (material.opacity !== undefined) {
      switch (material.type) {
        case 'color':
          group.colorOpacity = material.opacity;
          break;
        // For other types, opacity is already in the material object
      }
    }
  }

  /**
   * Recreate a Material instance from stored raw material data
   * This handles the format stored by storeOriginalMaterials (JSON copy of Material object)
   */
  recreateMaterialFromData(data) {
    if (!data || !data.type) return null;

    // Use Material.fromJSON which properly handles all material types
    // This works because JSON.stringify of a Material object produces the same
    // format that Material.fromJSON expects (via Material.toJSON serialization)
    return Material.fromJSON(data);
  }

  /**
   * Create Material object from PropertyPanel data
   */
  createMaterialFromData(data) {
    switch (data.type) {
      case 'color':
        return Material.color(data.color, data.opacity);

      case 'texture':
        const texMaterial = Material.texture(data.url, data.opacity);
        // Always set explicit defaults to ensure consistent rendering
        texMaterial.scaleX = data.scaleX !== undefined ? data.scaleX : 1;
        texMaterial.scaleY = data.scaleY !== undefined ? data.scaleY : 1;
        texMaterial.scaleZ = data.scaleZ !== undefined ? data.scaleZ : 1;
        texMaterial.offsetX = data.offsetX !== undefined ? data.offsetX : 0;
        texMaterial.offsetY = data.offsetY !== undefined ? data.offsetY : 0;
        texMaterial.offsetZ = data.offsetZ !== undefined ? data.offsetZ : 0;
        if (data.blendMode) texMaterial.blendMode = data.blendMode;
        return texMaterial;

      case 'gradient':
        // Material.gradient expects: (gradientType, colors[], direction, opacity)
        // For linear gradients, direction is the angle (e.g., 45 or '45deg')
        // For radial gradients, direction is the shape (e.g., 'circle' or 'ellipse')
        const isRadial = (data.gradientType || 'linear') === 'radial';
        const gradMaterial = Material.gradient(
          data.gradientType || 'linear',
          [data.color1, data.color2],
          isRadial ? 'circle' : (data.angle || 45),
          data.opacity
        );
        if (data.axis !== undefined) gradMaterial.axis = data.axis;
        if (data.scaleX !== undefined) gradMaterial.scaleX = data.scaleX;
        if (data.scaleY !== undefined) gradMaterial.scaleY = data.scaleY;
        if (data.scaleZ !== undefined) gradMaterial.scaleZ = data.scaleZ;
        if (data.offsetX !== undefined) gradMaterial.offsetX = data.offsetX;
        if (data.offsetY !== undefined) gradMaterial.offsetY = data.offsetY;
        if (data.offsetZ !== undefined) gradMaterial.offsetZ = data.offsetZ;
        return gradMaterial;

      case 'pattern':
        // Material.pattern expects: (patternType, options, opacity)
        // Build options based on pattern type
        let patternOptions = {};
        const patternType = data.patternType || data.pattern;

        if (patternType === 'stripes') {
          patternOptions = {
            colors: data.colors || [data.color1, data.color2],
            direction: data.direction || '0deg',
            size: data.size || '10px'
          };
        } else if (patternType === 'dots') {
          patternOptions = {
            backgroundColor: data.backgroundColor || '#ffffff',
            dotColor: data.dotColor || '#000000',
            dotSize: data.dotSize || '5px',
            spacing: data.spacing || '15px'
          };
        } else if (patternType === 'grid') {
          patternOptions = {
            backgroundColor: data.backgroundColor || '#ffffff',
            gridColor: data.gridColor || '#dddddd',
            gridSize: data.gridSize || '10px',
            lineWidth: data.lineWidth || '1px'
          };
        }

        const patMaterial = Material.pattern(
          patternType,
          patternOptions,
          data.opacity
        );
        return patMaterial;

      case 'video':
        const vidMaterial = Material.video(data.url, data.opacity);
        vidMaterial.autoplay = data.autoplay;
        vidMaterial.loop = data.loop;
        vidMaterial.muted = data.muted;
        if (data.scaleX !== undefined) vidMaterial.scaleX = data.scaleX;
        if (data.scaleY !== undefined) vidMaterial.scaleY = data.scaleY;
        if (data.scaleZ !== undefined) vidMaterial.scaleZ = data.scaleZ;
        if (data.offsetX !== undefined) vidMaterial.offsetX = data.offsetX;
        if (data.offsetY !== undefined) vidMaterial.offsetY = data.offsetY;
        if (data.offsetZ !== undefined) vidMaterial.offsetZ = data.offsetZ;
        return vidMaterial;

      case 'text':
        // Text material - explicitly set type to 'text' for proper detection
        const textMaterial = Material.color(data.bgColor, 1.0);
        textMaterial.type = 'text'; // Override type so it's detected as text, not color
        textMaterial.text = data.text;
        textMaterial.textColor = data.textColor;
        textMaterial.textSize = `${data.fontSize}px`;
        textMaterial.textFont = data.fontWeight === 'bold' ? 'bold Arial' : 'Arial';
        if (data.axis !== undefined) textMaterial.axis = data.axis;
        if (data.scaleX !== undefined) textMaterial.scaleX = data.scaleX;
        if (data.scaleY !== undefined) textMaterial.scaleY = data.scaleY;
        if (data.scaleZ !== undefined) textMaterial.scaleZ = data.scaleZ;
        if (data.offsetX !== undefined) textMaterial.offsetX = data.offsetX;
        if (data.offsetY !== undefined) textMaterial.offsetY = data.offsetY;
        if (data.offsetZ !== undefined) textMaterial.offsetZ = data.offsetZ;
        return textMaterial;

      case 'special':
        // Material.special expects: (effectType, color, opacity, options)
        const effectType = data.effectType || data.effect;
        const effectOptions = {};

        // Map effect-specific options (H/V directions only - rotated effects removed)
        if (effectType === 'metallic') {
          effectOptions.metallicDirection = data.metallicDirection || '0deg';
        } else if (effectType === 'holographic') {
          effectOptions.holographicDirection = data.holographicDirection || '0deg';
        }

        const effectMaterial = Material.special(
          effectType,
          data.color,
          data.opacity,
          effectOptions
        );
        return effectMaterial;

      case 'webpage':
        return Material.webpage(data.url, data.opacity, {
          interactive: data.interactive !== false
        });
    }

    return null;
  }
}
